import CaseElements from '../components/CaseElements';
import type { CaseElementsPanel } from '../components/CaseElements';
import type { ComplaintDraft } from '../util/complaint-rules';
import { EVIDENCE_KIND_LABEL, evidenceSummary, summarizeDraft } from '../util/complaint-rules';
import { WRITE_BUSY_BUTTON } from '../api/write-timeout';

/**
 * PC 端信息确认面板 —— 「提交投诉」按下之后、真正落库之前的那一屏。
 *
 * ★ 与手机端 `pages/SubmitConfirm.tsx` **同一份数据、同一套字段**：
 *   摆哪几行、每行叫什么、没填时显示什么，全部来自
 *   `util/complaint-rules.ts` 的 `summarizeDraft()`。两端只有版式不同，口径不许分叉。
 *   （PC 宽，短字段排成两列的定义表；手机窄，排成竖着的一列。）
 *
 * ★ 纯展示件，不持有草稿：草稿留在 `PcForm` 的 state 里，
 *   「返回修改」只是把画法换回表单，一个字都不会丢。
 *
 * ★ 投诉内容及诉求**全文照摆**（`white-space:pre-wrap`），不截断、不折叠 ——
 *   截成一行等于没法核对，这一屏就白加了。
 *
 * ★★ 2026-08-27 主公令：这一屏顶上多了一块「案件要素」（系统整理的五项，每项可改）。
 *   那一块是 `components/CaseElements.tsx` —— **与手机端确认页同一个组件**，
 *   这里只把它摆进 `.pcsheet` 那张纸的第一节（`variant="pc"` 切版式，不切逻辑）。
 *   摆在最上面的理由与手机端相同：这一屏上唯一需要他动手的就是这一块，
 *   压在几百字的投诉内容及诉求后面等于没人看得见。
 */
export default function PcConfirm({
  draft,
  busy,
  onBack,
  onConfirm,
  elements,
}: {
  draft: ComplaintDraft;
  busy: boolean;
  onBack: () => void;
  onConfirm: () => void;
  /** 「案件要素」那一块要的全部东西，原样传下去，本页不拆不改 */
  elements: CaseElementsPanel;
}) {
  const rows = summarizeDraft(draft);
  const text = draft.text.trim();

  return (
    <>
      <div className="pchero">
        <h1>确认投诉信息</h1>
        <p>
          下面是您刚才填写的内容，请再核对一遍。其中「案件要素」是系统按您写的投诉内容及诉求整理的，有出入请直接改。确认无误后提交，提交后如需补充材料，可在手机上打开房诉通继续。
        </p>
      </div>

      <div className="pcsheet">
        <CaseElements {...elements} variant="pc" />

        <section className="pcsec">
          <div className="pcsec-h">
            <h2>投诉内容及诉求</h2>
            <span className="pcsec-side">{text.length} 字</span>
          </div>
          <p className="pccfm-text">{text}</p>
        </section>

        <section className="pcsec">
          <div className="pcsec-h">
            <h2>证据材料</h2>
            <span className="pcsec-side">{evidenceSummary(draft.files)}</span>
          </div>
          <ul className="pcshots">
            {draft.files.map((f, i) => (
              <li className="pcshot" key={`${f.name}-${i}`}>
                <span className="pccfm-no">{i + 1}</span>
                {/* ★ 2026-09-04 批 ⑭：非图片画类别标记，见手机端确认页同处注释 */}
                {f.preview ? (
                  <img src={f.preview} alt={f.name} />
                ) : (
                  <span className="pcshot-kind">{EVIDENCE_KIND_LABEL[f.kind]}</span>
                )}
                <span className="pcshot-n" title={f.name}>
                  {f.name}
                </span>
              </li>
            ))}
          </ul>
        </section>

        <section className="pcsec">
          <div className="pcsec-h">
            <h2>您的信息</h2>
          </div>
          <dl className="pccfm-rows">
            {rows.map((r) => (
              <div className="pccfm-row" key={r.key}>
                <dt>{r.label}</dt>
                <dd className={r.blank ? 'blank' : undefined}>{r.value}</dd>
              </div>
            ))}
          </dl>
        </section>
      </div>

      <div className="pcsubmit pccfm-bar">
        <button type="button" className="pcbtn pcbtn-line pccfm-back" onClick={onBack} disabled={busy}>
          返回修改
        </button>
        {/* 措辞要让人知道按下去意味着什么：不写「确定」 */}
        <button type="button" className="pcbtn pcbtn-main" onClick={onConfirm} disabled={busy}>
          {busy ? WRITE_BUSY_BUTTON : '确认无误，提交投诉'}
        </button>
      </div>
    </>
  );
}
